export type DoctorDaySlotsRecord = {
  dateKey: string
  dayLabel: string
  fullDayLabel: string
  dateLabel: string
  slots: Array<{
    value: string
    time: string
    label: string
  }>
}

export type FormSlotOption = {
  value: string
  label: string
}

export const DOCTOR_SLOTS_CACHE_TTL_MS = 5 * 60_000
export const FORM_SLOTS_CACHE_TTL_MS = 5 * 60_000

type DoctorSlotsCacheEntry = {
  token: string
  cachedAt: number
  slots: DoctorDaySlotsRecord[]
}

type FormSlotsCacheEntry = {
  token: string
  cachedAt: number
  slots: FormSlotOption[]
}

const doctorSlotsCache = new Map<string, DoctorSlotsCacheEntry>()
const formSlotsCache = new Map<string, FormSlotsCacheEntry>()

function doctorKey(language: string, doctorId: string): string {
  return `${language}:${doctorId}`
}

function formKey(language: string, doctorId: string): string {
  return `${language}:${doctorId}`
}

export function getCachedDoctorSlots(
  language: string,
  doctorId: string,
  token: string,
): DoctorDaySlotsRecord[] | null {
  const entry = doctorSlotsCache.get(doctorKey(language, doctorId))
  if (!entry) return null
  if (entry.token !== token) return null
  if (Date.now() - entry.cachedAt > DOCTOR_SLOTS_CACHE_TTL_MS) return null
  return entry.slots
}

// Возвращает кэш игнорируя TTL — для stale-while-revalidate:
// показываем устаревшие слоты пока обновляем в фоне, чтобы
// при возврате со страницы регистрации UI не схлопывался в пустоту.
export function getStaleDoctorSlots(
  language: string,
  doctorId: string,
  token: string,
): DoctorDaySlotsRecord[] | null {
  const entry = doctorSlotsCache.get(doctorKey(language, doctorId))
  if (!entry) return null
  if (entry.token !== token) return null
  return entry.slots
}

export function setCachedDoctorSlots(
  language: string,
  doctorId: string,
  token: string,
  slots: DoctorDaySlotsRecord[],
): void {
  doctorSlotsCache.set(doctorKey(language, doctorId), {
    token,
    cachedAt: Date.now(),
    slots,
  })
}

export function invalidateDoctorSlotsCacheForToken(token: string): void {
  doctorSlotsCache.forEach((entry, key) => {
    if (entry.token === token) {
      doctorSlotsCache.delete(key)
    }
  })
}

export function getCachedFormSlots(
  language: string,
  doctorId: string,
  token: string,
): FormSlotOption[] | null {
  const entry = formSlotsCache.get(formKey(language, doctorId))
  if (!entry) return null
  if (entry.token !== token) return null
  if (Date.now() - entry.cachedAt > FORM_SLOTS_CACHE_TTL_MS) return null
  return entry.slots
}

export function setCachedFormSlots(
  language: string,
  doctorId: string,
  token: string,
  slots: FormSlotOption[],
): void {
  formSlotsCache.set(formKey(language, doctorId), {
    token,
    cachedAt: Date.now(),
    slots,
  })
}

export function invalidateFormSlotsCacheForToken(token: string): void {
  formSlotsCache.forEach((entry, key) => {
    if (entry.token === token) {
      formSlotsCache.delete(key)
    }
  })
}
